import React, { useState } from "react";
import {
  Modal,
  Spin,
  Steps,
  Table,
  Tag,
  Select,
  Input,
  Button,
  Divider,
  message,
  Card,
  Typography as AntTypography,
} from "antd";
import { Clock, AlertCircle } from "lucide-react";
import {
  useAdminOrderDetail,
  useUpdateOrderStatus,
  useUpdateOrderPaymentStatus,
} from "../../../hooks/api/order/hooks";
import {
  IOrderItem,
  IOrderStatusHistory,
  OrderStatusType,
  PaymentStatusType,
} from "../../../types/api/order";

const { Text } = AntTypography;

interface OrderDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  orderId: string | null;
}

const ORDER_STATUS_STEPS: { status: OrderStatusType; title: string }[] = [
  { status: "CONFIRMED", title: "Confirmed" },
  { status: "PROCESSING", title: "Processing" },
  { status: "DISPATCHED", title: "Dispatched" },
  { status: "SHIPPED", title: "Shipped" },
  { status: "IN_TRANSIT", title: "In Transit" },
  { status: "DELIVERED", title: "Delivered" },
  { status: "CANCELLED", title: "Cancelled" },
];

/**
 * Safely render location/address field value whether it is a string or populated object
 */
const renderLocationVal = (val: any): string => {
  if (!val) return "";
  if (typeof val === "string") return val;
  if (typeof val === "object") return val.name || val.title || val.label || val.slug || "";
  return String(val);
};

const renderFullAddress = (addr: any) => {
  if (!addr) return "N/A";
  const parts = [
    renderLocationVal(addr.address),
    renderLocationVal(addr.city),
    renderLocationVal(addr.state),
    renderLocationVal(addr.postalCode),
    renderLocationVal(addr.country),
  ].filter(Boolean);
  return parts.length > 0 ? parts.join(", ") : "N/A";
};

const OrderDetailModal: React.FC<OrderDetailModalProps> = ({
  isOpen,
  onClose,
  orderId,
}) => {
  const [commentModalOpen, setCommentModalOpen] = useState(false);
  const [pendingTargetStatus, setPendingTargetStatus] =
    useState<OrderStatusType | null>(null);
  const [statusComment, setStatusComment] = useState("");

  const { data, isLoading } = useAdminOrderDetail(orderId || undefined, {
    enabled: isOpen && !!orderId,
  });

  const updateStatusMutation = useUpdateOrderStatus();
  const updatePaymentStatusMutation = useUpdateOrderPaymentStatus();

  const order = data?.data;

  const handleStepClick = (stepIndex: number) => {
    const target = ORDER_STATUS_STEPS[stepIndex];
    if (!target || target.status === order?.orderStatus) return;

    setPendingTargetStatus(target.status);
    setStatusComment(`Order status updated to ${target.title}`);
    setCommentModalOpen(true);
  };

  const confirmStatusUpdate = () => {
    if (!orderId || !pendingTargetStatus) return;

    updateStatusMutation.mutate(
      {
        id: orderId,
        status: pendingTargetStatus,
        comment: statusComment,
      },
      {
        onSuccess: () => {
          message.success(`Status updated to ${pendingTargetStatus}`);
          setCommentModalOpen(false);
          setPendingTargetStatus(null);
          setStatusComment("");
        },
        onError: (err: any) => {
          message.error(
            err?.response?.data?.message ||
              err?.message ||
              "Failed to update order status",
          );
        },
      },
    );
  };

  const handlePaymentStatusChange = (newStatus: PaymentStatusType) => {
    if (!orderId) return;
    updatePaymentStatusMutation.mutate(
      {
        id: orderId,
        paymentStatus: newStatus,
      },
      {
        onSuccess: () => {
          message.success("Payment status updated successfully");
        },
        onError: (err: any) => {
          message.error(
            err?.response?.data?.message ||
              err?.message ||
              "Failed to update payment status",
          );
        },
      },
    );
  };

  const currentStatusIndex = ORDER_STATUS_STEPS.findIndex(
    (s) => s.status === order?.orderStatus,
  );

  const productColumns = [
    {
      title: "Sculpture",
      key: "product",
      render: (item: IOrderItem) => (
        <div className="flex items-center gap-3">
          {item.images?.[0] ? (
            <img
              src={item.images[0]}
              alt={item.title}
              className="w-10 h-10 object-cover rounded border border-slate-200"
            />
          ) : (
            <div className="w-10 h-10 bg-slate-100 rounded flex items-center justify-center text-slate-400 text-[10px]">
              No Image
            </div>
          )}
          <div>
            <div className="font-medium text-slate-800 text-xs">
              {item.title}
            </div>
            {item.sku && (
              <div className="text-[10px] text-slate-400">SKU: {item.sku}</div>
            )}
          </div>
        </div>
      ),
    },
    {
      title: "Price",
      dataIndex: "price",
      key: "price",
      render: (price: number) =>
        `£${price ? price.toLocaleString("en-GB") : "0"}`,
    },
    {
      title: "Qty",
      dataIndex: "quantity",
      key: "quantity",
    },
    {
      title: "Total",
      dataIndex: "subTotal",
      key: "subTotal",
      render: (subTotal: number) =>
        `£${subTotal ? subTotal.toLocaleString("en-GB") : "0"}`,
    },
  ];

  return (
    <>
      <Modal
        title={
          <div>
            <div className="text-lg font-bold text-slate-800">
              Order Details: {order?.orderNumber || orderId}
            </div>
            {order?.createdAt && (
              <div className="text-xs text-slate-400 font-normal">
                Placed on {new Date(order.createdAt).toLocaleString()}
              </div>
            )}
          </div>
        }
        width={1200}
        open={isOpen}
        onCancel={onClose}
        footer={null}
        destroyOnClose
        centered
        className="max-h-[90vh] overflow-y-auto"
      >
        {isLoading ? (
          <div className="flex justify-center items-center py-20">
            <Spin size="large" />
          </div>
        ) : order ? (
          <div className="space-y-6 pt-2">
            {/* Top Status Summary Bar */}
            <div className="grid grid-cols-2 gap-4 bg-slate-50 p-4 rounded-lg border border-slate-200">
              <div>
                <Text type="secondary" className="text-xs">
                  Payment Status
                </Text>
                <div className="mt-1">
                  <Select
                    value={order.paymentStatus}
                    onChange={handlePaymentStatusChange}
                    loading={updatePaymentStatusMutation.isPending}
                    className="w-36"
                    options={[
                      { value: "PENDING", label: "Pending" },
                      { value: "PAID", label: "Paid" },
                      { value: "FAILED", label: "Failed" },
                    ]}
                  />
                </div>
              </div>
              <div>
                <Text type="secondary" className="text-xs">
                  Current Order Status
                </Text>
                <div className="mt-1">
                  <Tag
                    color={
                      order.orderStatus === "DELIVERED"
                        ? "green"
                        : order.orderStatus === "CANCELLED"
                          ? "red"
                          : "blue"
                    }
                    className="px-3 py-1 text-sm font-semibold"
                  >
                    {order.orderStatus}
                  </Tag>
                </div>
              </div>
              {(order.stripeSessionId || order.stripePaymentIntentId) && (
                <div className="col-span-2 pt-2 border-t border-slate-200 text-[11px] text-slate-500 space-y-0.5">
                  {order.stripeSessionId && <div>Stripe Session: {order.stripeSessionId}</div>}
                  {order.stripePaymentIntentId && (
                    <div>Stripe Payment Intent: {order.stripePaymentIntentId}</div>
                  )}
                </div>
              )}
            </div>

            {/* Clickable Order Progress Stepper */}
            <Card
              title={
                <div>
                  <span className="font-semibold">
                    Click a Step to Update Status
                  </span>
                  <span className="text-xs text-slate-400 font-normal ml-2">
                    (Click any status step below to transition order state)
                  </span>
                </div>
              }
              size="small"
              className="border-slate-200"
            >
              {order.orderStatus === "CANCELLED" ? (
                <div className="flex items-center gap-2 text-red-600 font-medium py-2">
                  <AlertCircle className="w-5 h-5" />
                  This order has been cancelled.
                </div>
              ) : (
                <Steps
                  current={currentStatusIndex >= 0 ? currentStatusIndex : 0}
                  onChange={handleStepClick}
                  size="small"
                  className="cursor-pointer py-2"
                  items={ORDER_STATUS_STEPS.map((step) => ({
                    title: step.title,
                  }))}
                />
              )}
            </Card>

            {/* Customer & Address Details */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Card
                title="Shipping Address"
                size="small"
                className="border-slate-200"
              >
                {order.shippingAddress ? (
                  <div className="text-xs space-y-1 text-slate-700">
                    <div className="font-semibold text-slate-900 text-sm">
                      {renderLocationVal(order.shippingAddress.fullName) || "N/A"}
                    </div>
                    {order.shippingAddress.email && (
                      <div>{renderLocationVal(order.shippingAddress.email)}</div>
                    )}
                    {order.shippingAddress.phone && (
                      <div>Phone: {renderLocationVal(order.shippingAddress.phone)}</div>
                    )}
                    <div className="mt-2 text-slate-600">
                      {renderFullAddress(order.shippingAddress)}
                    </div>
                  </div>
                ) : (
                  <div className="text-xs text-slate-400">No Shipping Address Provided</div>
                )}
              </Card>

              <Card
                title="Billing Address"
                size="small"
                className="border-slate-200"
              >
                {order.billingAddress ? (
                  <div className="text-xs space-y-1 text-slate-700">
                    <div className="font-semibold text-slate-900 text-sm">
                      {renderLocationVal(order.billingAddress.fullName) || "N/A"}
                    </div>
                    {order.billingAddress.email && (
                      <div>{renderLocationVal(order.billingAddress.email)}</div>
                    )}
                    {order.billingAddress.phone && (
                      <div>Phone: {renderLocationVal(order.billingAddress.phone)}</div>
                    )}
                    <div className="mt-2 text-slate-600">
                      {renderFullAddress(order.billingAddress)}
                    </div>
                  </div>
                ) : (
                  <div className="text-xs text-slate-400">No Billing Address Provided</div>
                )}
              </Card>
            </div>

            {/* Products Snapshot Table */}
            <Card
              title="Ordered Items"
              size="small"
              className="border-slate-200"
            >
              <Table
                columns={productColumns}
                dataSource={order.items || []}
                pagination={false}
                rowKey={(item) => item._id || item.productId}
                size="small"
              />
              <Divider className="my-3" />
              <div className="flex flex-col items-end space-y-1 text-xs text-slate-600">
                <div className="flex justify-between w-48">
                  <span>Subtotal:</span>
                  <span>
                    £{order.price?.subTotal?.toLocaleString("en-GB") || 0}
                  </span>
                </div>
                <div className="flex justify-between w-48">
                  <span>Shipping:</span>
                  <span>
                    £{order.price?.shippingFee?.toLocaleString("en-GB") || 0}
                  </span>
                </div>
                <div className="flex justify-between w-48 font-bold text-sm text-slate-900 pt-2 border-t border-slate-200">
                  <span>Total Amount:</span>
                  <span>
                    £{order.price?.totalAmount?.toLocaleString("en-GB") || 0}
                  </span>
                </div>
              </div>
            </Card>

            {/* Status History Timeline Audit Log */}
            <Card
              title="Status Tracking Audit Log"
              size="small"
              className="border-slate-200"
            >
              <div className="space-y-2 max-h-48 overflow-y-auto pr-2">
                {order.trackingHistory?.map((h: IOrderStatusHistory) => (
                  <div
                    key={h._id}
                    className="flex items-start gap-3 p-2.5 bg-slate-50 rounded border border-slate-100 text-xs"
                  >
                    <Clock className="w-3.5 h-3.5 text-slate-400 mt-0.5" />
                    <div className="flex-1">
                      <div className="flex justify-between items-center">
                        <Tag color="blue" className="font-semibold text-[11px]">
                          {h.status}
                        </Tag>
                        <span className="text-[10px] text-slate-400">
                          {new Date(h.createdAt).toLocaleString()}
                        </span>
                      </div>
                      {h.comment && (
                        <p className="text-slate-600 text-[11px] mt-1">
                          {h.comment}
                        </p>
                      )}
                      <span className="text-[10px] text-slate-400">
                        By: {h.createdBy || "System"}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        ) : (
          <div className="text-center py-20 text-slate-500">
            Order not found.
          </div>
        )}
      </Modal>

      {/* Confirmation & Optional Comment Modal when Clicking a Step */}
      <Modal
        title={`Update Order Status to ${pendingTargetStatus}`}
        open={commentModalOpen}
        onCancel={() => {
          setCommentModalOpen(false);
          setPendingTargetStatus(null);
        }}
        onOk={confirmStatusUpdate}
        confirmLoading={updateStatusMutation.isPending}
        okText="Confirm & Update Status"
        destroyOnClose
      >
        <div className="space-y-3 py-2">
          <p className="text-sm text-slate-600">
            Are you sure you want to change the order status to{" "}
            <strong className="text-slate-900">{pendingTargetStatus}</strong>?
          </p>
          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">
              Tracking Note / Comment (Optional)
            </label>
            <Input.TextArea
              rows={2}
              value={statusComment}
              onChange={(e) => setStatusComment(e.target.value)}
              placeholder="e.g. Package dispatched via DHL Express #123456"
            />
          </div>
        </div>
      </Modal>
    </>
  );
};

export default OrderDetailModal;
